import { FC } from "react";
import { InnerBox } from "@/components/elements/InnerBox";
import { Box, FormLabel, Grid, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import { useFormContext, useWatch } from "react-hook-form";
import { TPartnerForm } from "@/features/rso/types/partnerForm";
import { Select } from "@/components/elements/Select";
import { ERSOContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { Checkbox } from "@/components/elements/Checkbox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { EMPTY_STR } from "@/constants/string";
import { IconErrorOutline } from "@/assets/icons";
import { black, red } from "@/theme/colors";
import { Tooltip } from "@/components/elements/Tooltip";

type TContractFormProps = {};
export const ContractForm: FC<TContractFormProps> = () => {
  const methods = useFormContext<TPartnerForm>();
  const { control, watch, setValue } = methods;

  const contractStatus = watch("contractStatus");

  const trialEndDateWatchField = useWatch({ control, name: "trialEndDate" });
  const trialStartDateWatchField = useWatch({ control, name: "trialStartDate" });
  const contractEndDateWatchField = useWatch({ control, name: "contractEndDate" });
  const contractStartDateWatchField = useWatch({ control, name: "contractStartDate" });

  const handleChangeContractStatus = (event: SelectChangeEvent<unknown>) => {
    if (event.target.value === ERSOContractStatus.TRIAL) {
      if (!trialStartDateWatchField) {
        methods.setValue("trialStartDate", dayjs());
      }
      if (!trialEndDateWatchField) {
        const newDate = dayjs(trialStartDateWatchField || dayjs()).add(2, "weeks");
        setValue("trialEndDate", newDate);
      }
    }
    if (event.target.value === ERSOContractStatus.DURING_CONTRACT && !contractStartDateWatchField) {
      methods.setValue("contractStartDate", dayjs(), {
        shouldValidate: true,
      });
    }
  };
  return (
    <InnerBox title="契約情報">
      <Grid container rowSpacing={2} columnSpacing={3}>
        <Grid item xs={6}>
          <FormField control={control} name="contractStatus" label="">
            <Stack direction="row" alignItems="center" gap={0.5}>
              <FormLabel required sx={{ fontSize: "12px", color: red[700] }}>
                <Typography variant="cap12" color={black[70]}>
                  ステータス
                </Typography>
              </FormLabel>
              <Tooltip title="" content="「トライアル開始・終了日、契約日、解約日の入力内容に応じて自動変更されます。AM0時に更新されます。」">
                <Box component="span" mb={0.5} sx={{ display: "inline-flex", verticalAlign: "middle", lineHeight: 0 }}>
                  <IconErrorOutline width={15} height={15} color={black[80]} />
                </Box>
              </Tooltip>
            </Stack>
            <Select
              enumName="ERSOContractStatus"
              onChange={handleChangeContractStatus}
              placeholder="トライアル中/トライアル終了 / 契約中 / 解約済み"
            />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="contractPlan" label="契約プラン">
            <Input placeholder="入力してください" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="trialStartDate" label="トライアル開始日">
            <InputDatePicker placeholder={EMPTY_STR.DATE} format="YYYY/MM/DD" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="trialEndDate" label="トライアル終了日">
            <InputDatePicker placeholder={EMPTY_STR.DATE} format="YYYY/MM/DD" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="contractStartDate" label="契約日" required={contractStatus === ERSOContractStatus.DURING_CONTRACT}>
            <InputDatePicker placeholder={EMPTY_STR.DATE} maxDate={contractEndDateWatchField ?? undefined} format="YYYY/MM/DD" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="contractEndDate" label="解約日">
            <InputDatePicker placeholder={EMPTY_STR.DATE} format="YYYY/MM/DD" />
          </FormField>
        </Grid>
        <Grid item xs={12}>
          <FormField control={control} name="webAccessEnabled" label="">
            <Checkbox label="学習アプリをWebから利用可能にする" subLabel="Web版は社内に限定されます。お客様環境では常にOFFにしてください。" />
          </FormField>
        </Grid>
      </Grid>
    </InnerBox>
  );
};
